import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { Turbine } from '../types';
import { listProjects } from '../utils/r2Storage';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  h2 {
    font-size: 24px;
    font-weight: 700;
    color: #1a1f3a;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: #6b7280;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #0099ff;
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #0099ff;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 153, 255, 0.3);
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InspectionList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px;
`;

const InspectionItem = styled.div<{ $selected: boolean }>`
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$selected ? '#e0f2fe' : 'white'};
  border: 2px solid ${props => props.$selected ? '#0099ff' : '#e5e7eb'};
  
  &:hover {
    background: #f0f9ff;
    border-color: #0099ff;
  }
`;

interface TurbineModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (turbine: Omit<Turbine, 'id' | 'createdAt' | 'farmId'>) => void;
    turbine?: Turbine;
}

const TurbineModal: React.FC<TurbineModalProps> = ({ isOpen, onClose, onSave, turbine }) => {
    const [formData, setFormData] = useState({
        number: turbine?.number || '',
        name: turbine?.name || '',
        manufacturer: turbine?.manufacturer || '',
        model: turbine?.model || '',
        bladeLength: turbine?.bladeLength || 0,
        bladeDiameter: turbine?.bladeDiameter || 0,
        power: turbine?.power || 0,
        lat: turbine?.location?.lat || 39.0,
        lng: turbine?.location?.lng || 35.0,
        inspectionId: turbine?.inspectionId || '',
    });

    const [inspections, setInspections] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            loadInspections();
        }
    }, [isOpen]);

    const loadInspections = async () => {
        try {
            const projects = await listProjects();
            setInspections(projects);
        } catch (error) {
            console.error('Failed to load inspections:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        onSave({
            number: formData.number,
            name: formData.name,
            manufacturer: formData.manufacturer,
            model: formData.model,
            bladeLength: formData.bladeLength,
            bladeDiameter: formData.bladeDiameter,
            power: formData.power,
            location: { lat: formData.lat, lng: formData.lng },
            inspectionId: formData.inspectionId,
            lastInspectionDate: formData.inspectionId ? new Date().toISOString() : undefined,
            damageCount: 0,
            rotorDiameter: formData.bladeDiameter,
            bladeModel: '',
            turbineNumber: formData.number,
        });

        onClose();
    };

    const handleChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <Header>
                    <h2>{turbine ? 'Edit Turbine' : 'Add Turbine'}</h2>
                    <CloseButton onClick={onClose}>
                        <X />
                    </CloseButton>
                </Header>

                <Form onSubmit={handleSubmit}>
                    <FormRow>
                        <FormGroup>
                            <Label>Turbine Number *</Label>
                            <Input
                                type="text"
                                value={formData.number}
                                onChange={(e) => handleChange('number', e.target.value)}
                                placeholder="e.g. T01"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Turbine Name *</Label>
                            <Input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g. North Turbine 1"
                                required
                            />
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <Label>Manufacturer *</Label>
                            <Input
                                type="text"
                                value={formData.manufacturer}
                                onChange={(e) => handleChange('manufacturer', e.target.value)}
                                placeholder="e.g. Vestas"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Model *</Label>
                            <Input
                                type="text"
                                value={formData.model}
                                onChange={(e) => handleChange('model', e.target.value)}
                                placeholder="e.g. V90-3.0"
                                required
                            />
                        </FormGroup>
                    </FormRow>

                    <FormRow>
                        <FormGroup>
                            <Label>Blade Length (m)</Label>
                            <Input
                                type="number"
                                step="0.1"
                                value={formData.bladeLength}
                                onChange={(e) => handleChange('bladeLength', parseFloat(e.target.value))}
                                placeholder="e.g. 44.0"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Rotor Diameter (m)</Label>
                            <Input
                                type="number"
                                step="0.1"
                                value={formData.bladeDiameter}
                                onChange={(e) => handleChange('bladeDiameter', parseFloat(e.target.value))}
                                placeholder="e.g. 90.0"
                            />
                        </FormGroup>
                    </FormRow>

                    <FormGroup>
                        <Label>Power (MW)</Label>
                        <Input
                            type="number"
                            step="0.1"
                            value={formData.power}
                            onChange={(e) => handleChange('power', parseFloat(e.target.value))}
                            placeholder="e.g. 3.0"
                        />
                    </FormGroup>

                    <FormRow>
                        <FormGroup>
                            <Label>Latitude</Label>
                            <Input
                                type="number"
                                step="0.000001"
                                value={formData.lat}
                                onChange={(e) => handleChange('lat', parseFloat(e.target.value))}
                                placeholder="e.g. 39.0"
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Longitude</Label>
                            <Input
                                type="number"
                                step="0.000001"
                                value={formData.lng}
                                onChange={(e) => handleChange('lng', parseFloat(e.target.value))}
                                placeholder="e.g. 35.0"
                            />
                        </FormGroup>
                    </FormRow>

                    <FormGroup>
                        <Label>Linked Inspection</Label>
                        {loading ? (
                            <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading inspections...</p>
                        ) : inspections.length === 0 ? (
                            <p style={{ color: '#6b7280', fontSize: '14px' }}>No inspections available</p>
                        ) : (
                            <InspectionList>
                                <InspectionItem
                                    $selected={formData.inspectionId === ''}
                                    onClick={() => handleChange('inspectionId', '')}
                                >
                                    None (No inspection linked)
                                </InspectionItem>
                                {inspections.map(inspection => (
                                    <InspectionItem
                                        key={inspection.id}
                                        $selected={formData.inspectionId === inspection.id}
                                        onClick={() => handleChange('inspectionId', inspection.id)}
                                    >
                                        {inspection.id}
                                    </InspectionItem>
                                ))}
                            </InspectionList>
                        )}
                    </FormGroup>

                    <ButtonGroup>
                        <Button type="button" $variant="secondary" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" $variant="primary">
                            {turbine ? 'Save Changes' : 'Add Turbine'}
                        </Button>
                    </ButtonGroup>
                </Form>
            </ModalContainer>
        </Overlay>
    );
};

export default TurbineModal;
